import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Offer } from '@wwt/entities/search-response'
import { extractFeaturesFromOffer } from '@wwt/shared/utils'

import { generateReservationTab } from '@/features/ReservationTab'
import { useToast } from '@/shared/lib/hooks/useToast'

import { TabValues } from '../types'
import {
	HotelSection,
	LocationSection,
	OtherRoomsSection,
	RoomSection
} from '../ui/RoomInfoTabs'
import { generateRemoteTabs } from '../utils'

export const useTabs = (offer: Offer | null) => {
	const { t } = useTranslation('room-page')

	const features = extractFeaturesFromOffer(offer)
	const remoteTabs = generateRemoteTabs(features, offer, t)
	const reservationTab = generateReservationTab(offer, t)

	const { toast } = useToast()

	const scrollToTopWithToast = useCallback(() => {
		setTimeout(() => {
			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
		}, 150)
		toast({
			layer: 'modals',
			description: t('toastTexts.roomWasChanged', { ns: 'translation' })
		})
	}, [toast, t])

	const tabs: TabValues[] = useMemo(
		() => [
			...reservationTab,
			{
				label: t('room'),
				component: RoomSection,
				hint: t('tabHint', { tabName: t('room') })
			},
			{
				label: offer?.hotelStatic.accommodationType ?? t('hotel'),
				component: HotelSection,
				hint: t('tabHint', {
					tabName: offer?.hotelStatic.accommodationType ?? t('hotel')
				})
			},
			{
				label: t('location'),
				component: LocationSection,
				hint: t('tabHint', { tabName: t('location') })
			},
			{
				label: t('otherRooms.tabName'),
				props: { callback: scrollToTopWithToast },
				component: OtherRoomsSection,
				hint: t('tabHintForOtherRooms')
			},
			...remoteTabs
		],
		[offer, remoteTabs, reservationTab, t]
	)

	return tabs
}
